import * as S from "./HealthList.style";
import { Buttons } from "@/components/atoms/Buttons";
import { AlertText } from "@/components/atoms/AlertText";
import { useLocation, useNavigate } from "react-router";
import { ReactComponent as IconCamera } from "@/styles/assets/svg/icon_camera.svg";
import {
  HealthItemContent,
  HealthItemTitle,
} from "@/components/atoms/HealthItem";

interface HealthListProps {
  data: any;
  activeTab: string;
  spineIncluded?: boolean;
  handlePhotoConsent?: () => void;
}

export const HealthList = ({
  data = {},
  activeTab,
  spineIncluded = true,
  handlePhotoConsent,
}: HealthListProps) => {
  const navigate = useNavigate();
  const currentLocation = useLocation();

  if (activeTab === "physical") {
    return (
      <S.HealthList>
        {Object.keys(data).length === 1 &&
        currentLocation.pathname === "/spine" ? (
          <S.HealthInfoBtn>
            <AlertText
              mainText="수집된 건강 정보가 없습니다."
              subText="건강 및 신체기능 정보 입력이 필요합니다."
              button={
                <Buttons
                  type="button"
                  size="md"
                  width="176px"
                  layout="outline"
                  color="primary100"
                  label="건강 정보 입력하기"
                  onClick={() => navigate("/setting/health-info")}
                />
              }
            />
          </S.HealthInfoBtn>
        ) : (
          <>
            <S.HealthItem key={`physical_1`}>
              <HealthItemTitle title={`윗몸 앞으로 굽히기 검사`} />
              <HealthItemContent
                items={[
                  {
                    name: "거리",
                    data: `${data?.sitAndReach?.value ?? 0} cm`,
                    status: data?.sitAndReach?.status,
                    modal: true,
                    subject: "sitAndReach",
                  },
                ]}
              />
            </S.HealthItem>
            <S.HealthItem key={`physical_2`}>
              <HealthItemTitle title={`악력`} />
              <HealthItemContent
                items={[
                  {
                    name: "오른손",
                    data: `${data?.gripStrength?.right?.value ?? 0} kg`,
                    status: data?.gripStrength?.right?.status,
                    modal: true,
                    subject: "gribStrength",
                  },
                  {
                    name: "왼손",
                    data: `${data?.gripStrength?.left?.value ?? 0} kg`,
                    status: data?.gripStrength?.left?.status,
                    modal: true,
                    subject: "gribStrength",
                  },
                ]}
              />
            </S.HealthItem>
          </>
        )}
      </S.HealthList>
    );
  }

  return (
    <S.HealthList>
      {spineIncluded && (
        <S.HealthItem key={`health_1`}>
          <HealthItemTitle
            title={`척추건강`}
            button={
              <Buttons
                type="button"
                size="sm"
                width="140px"
                layout="outline"
                color="primary100"
                icon={<IconCamera width={20} height={20} />}
                label="척추 사진 재등록"
                onClick={handlePhotoConsent}
              />
            }
          />
          <S.HealthItemNote>
            <HealthItemContent
              items={[
                {
                  name: "척추측만증 확률",
                  data: `${data?.scoliosisProbability ?? 0} %`,
                  modal: false,
                },
              ]}
            />
            <span>
              &#8251; 수치는 가능성을 의미하며 확진을 의미하지는 않습니다.
            </span>
          </S.HealthItemNote>
        </S.HealthItem>
      )}

      <S.HealthItem key={`health_2`}>
        <HealthItemTitle title={`기본검진 정보`} />
        <HealthItemContent
          items={[
            {
              name: "키",
              data: `${data?.height ?? 0} cm`,
              modal: false,
            },
            {
              name: "몸무게",
              data: `${data?.weight ?? 0} kg`,
              modal: false,
            },
            {
              name: "BMI(체질량지수)",
              data: `${data?.bmi?.value ?? 0} kg/m²`,
              status: data?.bmi?.status,
              modal: true,
              subject: "bmi",
            },
          ]}
        />
        <HealthItemContent
          items={[
            {
              name: "골격근량",
              data: `${data?.skeletalMuscleMass?.value ?? 0} kg`,
              status: data?.skeletalMuscleMass?.status,
              modal: true,
              subject: "skeletalMuscleMass",
            },
            {
              name: "체지방률",
              data: `${data?.bodyFatPercentage?.value ?? 0} %`,
              status: data?.bodyFatPercentage?.status,
              modal: true,
              subject: "bodyFatPercentage",
            },
          ]}
        />
      </S.HealthItem>
    </S.HealthList>
  );
};
